//schema with only calculations to prepare the data for the projections page
import FormUIOptions from '../components/formBuilder/formElements/formUIOptions';
import { isValue } from '../utility/isValue';
import { mortgagePrincipalOutstanding } from '../utility/mortgageCalculator';
import { getNumberFromString } from '../utility/formatNumbers';
import getSum from '../utility/getSum';
import { yearlyPercentFromYearlyValue } from '../utility/percentageConverters';

///tbd equity / ltv
// this schema is used to calculate the projections for the property, it is not currently saved in the db.
// basically, there is a loop which goes through the years and calculates the projections for each year based on the previous year.
export default {
  calculations: {
    schema: [
      {
        sectionLabel: 'On Purchase',
        sectionValue: 'onPurchase',
        sectionDescription: 'Projections for the property on purchase',
        projectionSection: true,
        onlyBeforeData: true,
        mainField: {
          type: FormUIOptions.Number,
          unit: '%',
          name: 'capRate',
          label: 'Cap Rate',
          layout: 'default',
          dynamic: true,
        },
        schema: [
          {
            type: FormUIOptions.Header,
            label: 'Income',
          },
          {
            type: FormUIOptions.Money,
            label: 'Gross Rent',
            layout: 'default',
            name: 'rentalIncomePreVacancy',
            valueType: 'monthlyDollar',
            multiplier: 'incomeIncrease',
          },
          {
            type: FormUIOptions.Money,
            label: 'Vacancy',
            layout: 'default',
            name: 'vacancy',
            formula: (data) => ['-', data],
            valueType: 'monthlyDollar',
            derivedFrom: ['vacancyRate', 'rentalIncomePreVacancy'],
            calcFunction: (vacancyRate, rentalIncomePreVacancy) => {
              if (isValue(vacancyRate, rentalIncomePreVacancy)) {
                return (
                  Math.round(vacancyRate * rentalIncomePreVacancy * 100) / 100
                );
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Additional Income',
            layout: 'default',
            name: 'totalAdditionalIncome',
            formula: (data) => ['+', data],
            valueType: 'monthlyDollar',
            multiplier: 'incomeIncrease',
          },
          {
            type: FormUIOptions.Money,
            label: 'Gross Operating Income',
            layout: 'default',
            name: 'monthlyGOI',
            formula: (data) => ['=', data],
            emphasize: true,
            total: true,
            valueType: 'monthlyDollar',
            multiplier: 'incomeIncrease',
          },
          {
            type: FormUIOptions.Number,
            label: 'Income Increase',
            description: 'Assumed yearly increase in income',
            unit: '%',
            layout: 'default',
            name: 'yearlyIncomeIncrease',
            valueType: 'constantPercent',
            groupEnd: true,
            small: true,
          },
          {
            type: FormUIOptions.Header,
            label: 'Expenses',
          },
          {
            type: FormUIOptions.Money,
            label: 'Total Operating Expenses',
            layout: 'default',
            name: 'totalMonthlyOperatingExpensesPurchase',
            formula: (data) => ['-', data],
            valueType: 'monthlyDollar',
            multiplier: 'expensesIncrease',
          },
          {
            type: FormUIOptions.Number,
            label: 'Expense Increase',
            description: 'Assumed yearly increase in expenses',
            unit: '%',
            layout: 'default',
            name: 'yearlyExpenseIncrease',
            valueType: 'constantPercent',
            small: true,
          },
          {
            type: FormUIOptions.Money,
            label: 'Net Operating Income',
            layout: 'default',
            name: 'monthlyNOI',
            formula: (data) => ['=', data],
            emphasize: true,
            total: true,
            groupEnd: true,
            valueType: 'monthlyDollar',
            derivedFrom: [
              'monthlyGOI',
              'totalMonthlyOperatingExpensesPurchase',
            ],
            calcFunction: (monthlyGOI, totalMonthlyOperatingExpenses) => {
              if (isValue(monthlyGOI, totalMonthlyOperatingExpenses)) {
                return (
                  Math.round(
                    (monthlyGOI - totalMonthlyOperatingExpenses) * 100
                  ) / 100
                );
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Header,
            label: 'Cash Flow',
          },
          {
            type: FormUIOptions.Money,
            label: 'Loan Payments',
            description: 'Total of all mortgage payments',
            layout: 'default',
            name: 'monthlyDebtService',
            formula: (data) => ['-', data],
            valueType: 'monthlyDollar',
            derivedFrom: [
              'amortization',
              'monthlyPayment',
              'mortgageInterestOnly',
              'interestOnlyLoanPeriod',
              'purchaseAdditionalFinancing',
              'year',
              'cashPurchase',
            ],
            calcFunction: (
              amortization,
              monthlyPayment,
              mortgageInterestOnly,
              interestOnlyLoanPeriod,
              purchaseAdditionalFinancing,
              year,
              cashPurchase
            ) => {
              if (cashPurchase) return 0;
              let firstMonthlyPayment = 0;
              let additionalPayments = 0;

              if (mortgageInterestOnly) {
                if (year <= Math.floor(interestOnlyLoanPeriod)) {
                  firstMonthlyPayment = monthlyPayment;
                }
              } else {
                if (year <= amortization) {
                  firstMonthlyPayment = monthlyPayment;
                }
              }

              if (Array.isArray(purchaseAdditionalFinancing)) {
                purchaseAdditionalFinancing.forEach((item) => {
                  const {
                    mortgageInterestOnlyTable,
                    interestOnlyLoanPeriodTable,
                    monthlyPaymentTable,
                    amortizationTable,
                  } = item || {};

                  if (mortgageInterestOnlyTable) {
                    if (year <= interestOnlyLoanPeriodTable) {
                      additionalPayments += monthlyPaymentTable;
                    }
                  } else {
                    if (year <= amortizationTable) {
                      additionalPayments += monthlyPaymentTable;
                    }
                  }
                });
              }
              return (firstMonthlyPayment + additionalPayments) * 12;
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Balloon Payments',
            layout: 'default',
            name: 'balloonPayment',
            description:
              'Total of all balloon payments (if any) paid at the end of the loan period',
            formula: (data) => ['-', data],
            derivedFrom: [
              'mortgageAmount',
              'mortgageInterestOnly',
              'interestOnlyMonthly',
              'interestOnlyLoanPeriod',
              'interestRate',
              'purchaseAdditionalFinancing',
              'year',
              'cashPurchase',
            ],
            calcFunction: (
              mortgageAmount,
              mortgageInterestOnly,
              interestOnlyMonthly,
              interestOnlyLoanPeriod,
              interestRate,
              purchaseAdditionalFinancing,
              year,
              cashPurchase
            ) => {
              if (cashPurchase) return 0;
              let firstMortgageBalloon = 0;
              let additionalBalloon = 0;

              if (
                year === Math.floor(interestOnlyLoanPeriod) &&
                mortgageInterestOnly
              ) {
                firstMortgageBalloon = mortgageAmount;
                if (!interestOnlyMonthly) {
                  firstMortgageBalloon +=
                    mortgageAmount *
                    (interestRate / 100) *
                    interestOnlyLoanPeriod;
                }
              }

              if (Array.isArray(purchaseAdditionalFinancing)) {
                purchaseAdditionalFinancing.forEach((item) => {
                  const {
                    mortgageInterestOnlyTable,
                    interestOnlyLoanPeriodTable,
                    interestRateTable,
                    mortgageAmountTable,
                    interestOnlyMonthlyTable,
                  } = item || {};

                  if (
                    year === Math.floor(interestOnlyLoanPeriodTable) &&
                    mortgageInterestOnlyTable
                  ) {
                    additionalBalloon += mortgageAmountTable;
                    if (!interestOnlyMonthlyTable) {
                      additionalBalloon +=
                        mortgageAmountTable *
                        (interestRateTable / 100) *
                        interestOnlyLoanPeriodTable;
                    }
                  }
                });
              }

              return firstMortgageBalloon + additionalBalloon;
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Cash Flow',
            layout: 'default',
            name: 'annualPreTaxCashFlow',
            formula: (data) => ['=', data],
            emphasize: true,
            total: true,
            groupEnd: true,
            dynamic: true,
            valueType: 'yearlyDollar',
            derivedFrom: [
              'monthlyGOI',
              'totalMonthlyOperatingExpensesPurchase',
              'monthlyDebtService',
              'balloonPayment',
            ],
            calcFunction: (
              monthlyGOI,
              totalMonthlyOperatingExpenses,
              monthlyDebtService,
              balloonPayment
            ) => {
              if (
                isValue(
                  monthlyGOI,
                  totalMonthlyOperatingExpenses,
                  monthlyDebtService
                )
              ) {
                if (!balloonPayment) balloonPayment = 0;
                return (
                  Math.round(
                    (monthlyGOI -
                      monthlyDebtService -
                      totalMonthlyOperatingExpenses -
                      balloonPayment) *
                      100
                  ) / 100
                );
              } else {
                return null;
              }
            },
          },

          {
            type: FormUIOptions.Header,
            label: 'Key Metrics',
          },
          {
            type: FormUIOptions.Number,
            label: 'Cash on Cash',
            layout: 'default',
            name: 'cashOnCash',
            valueType: 'yearlyDollar',
            unit: '%',
            derivedFrom: [
              'annualPreTaxCashFlow',
              'downPaymentAmount',
              'totalCashRequiredToClose',
              'cashPurchase',
              'purchasePrice',
            ],
            calcFunction: (
              annualPreTaxCashFlow,
              downPaymentAmount,
              totalCashRequiredToClose,
              cashPurchase,
              purchasePrice
            ) => {
              const downPayment = cashPurchase
                ? purchasePrice
                : downPaymentAmount;

              return (
                yearlyPercentFromYearlyValue(annualPreTaxCashFlow, [
                  downPayment,
                  totalCashRequiredToClose,
                ]) || ' -'
              );
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Principal Recapture',
            layout: 'default',
            name: 'yearOnePrincipalReduction',
            formula: (data) => ['+', data],
            valueType: 'yearlyDollar',
            derivedFrom: [
              'yearOnePrincipalReduction',
              'interestRate',
              'mortgageAmount',
              'monthlyPayment',
              'year',
              'amortization',
              'mortgageInterestOnly',
              'interestOnlyLoanPeriod',
              'cashPurchase',
              'purchaseAdditionalFinancing',
            ],
            calcFunction: (
              yearOnePrincipalReduction,
              interestRate,
              mortgageAmount,
              monthlyPayment,
              year,
              amortization,
              mortgageInterestOnly,
              interestOnlyLoanPeriod,
              cashPurchase,
              purchaseAdditionalFinancing
            ) => {
              if (cashPurchase) return null;

              if (!isValue(year)) year = 1;
              if (year === 1) return yearOnePrincipalReduction;

              let firstMortgageReduction = 0;
              let additionalReduction = 0;

              if (isValue(interestRate, mortgageAmount, monthlyPayment)) {
                if (year <= amortization && !mortgageInterestOnly) {
                  const firstMortgageOutstandingAmount =
                    mortgagePrincipalOutstanding(
                      interestRate,
                      getNumberFromString(mortgageAmount),
                      getNumberFromString(monthlyPayment),
                      year * 12
                    );

                  const firstMortagePrincipalOutstandingPrev =
                    year === 1
                      ? mortgageAmount
                      : mortgagePrincipalOutstanding(
                          interestRate,
                          getNumberFromString(mortgageAmount),
                          getNumberFromString(monthlyPayment),
                          (year - 1) * 12
                        );

                  const prevYearsReduction =
                    Math.round(
                      getNumberFromString(mortgageAmount) * 100 -
                        firstMortagePrincipalOutstandingPrev * 100
                    ) / 100;

                  firstMortgageReduction =
                    Math.round(
                      getNumberFromString(mortgageAmount) * 100 -
                        firstMortgageOutstandingAmount * 100
                    ) /
                      100 -
                    prevYearsReduction;
                }

                if (
                  year === Math.floor(interestOnlyLoanPeriod) &&
                  mortgageInterestOnly
                ) {
                  firstMortgageReduction = mortgageAmount;
                }

                if (Array.isArray(purchaseAdditionalFinancing)) {
                  purchaseAdditionalFinancing.forEach((item) => {
                    const {
                      mortgageInterestOnlyTable,
                      interestOnlyLoanPeriodTable,
                      interestRateTable,
                      monthlyPaymentTable,
                      amortizationTable,
                      mortgageAmountTable,
                    } = item || {};

                    let mortgageReduction = 0;

                    if (
                      !mortgageInterestOnlyTable &&
                      year <= amortizationTable
                    ) {
                      const outstandingAmount = mortgagePrincipalOutstanding(
                        interestRateTable,
                        getNumberFromString(mortgageAmountTable),
                        getNumberFromString(monthlyPaymentTable),
                        year * 12
                      );

                      const outstandingPrev =
                        year === 1
                          ? mortgageAmount
                          : mortgagePrincipalOutstanding(
                              interestRateTable,
                              getNumberFromString(mortgageAmountTable),
                              getNumberFromString(monthlyPaymentTable),
                              (year - 1) * 12
                            );

                      const prevYearsReduction =
                        Math.round(
                          getNumberFromString(mortgageAmountTable) * 100 -
                            outstandingPrev * 100
                        ) / 100;

                      mortgageReduction =
                        Math.round(
                          getNumberFromString(mortgageAmountTable) * 100 -
                            outstandingAmount * 100
                        ) /
                          100 -
                        prevYearsReduction;
                    }

                    if (
                      mortgageInterestOnlyTable &&
                      typeof interestOnlyLoanPeriodTable === 'number' &&
                      year === Math.floor(interestOnlyLoanPeriodTable)
                    ) {
                      mortgageReduction = mortgageAmountTable;
                    }

                    additionalReduction += mortgageReduction;
                  });
                }

                return firstMortgageReduction + additionalReduction;
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Passive Appreciation',
            layout: 'default',
            name: 'marketAppreciationAmountPurchase',
            formula: (data) => ['+', data],
            valueType: 'yearlyDollar',
            multiplier: 'passiveAppreciation',
          },
          {
            type: FormUIOptions.Number,
            label: 'ROI ($)',
            unit: '%',
            name: 'totalRoiYear1Amount',
            description:
              'Percentage based on the total cash invested on purchase',
            formula: (data) => ['=', data],
            emphasize: true,
            dynamic: true,
            total: true,
            valueType: 'calculatedPercent',
            derivedFrom: [
              'downPaymentAmount',
              'totalCashRequiredToClose',
              'yearOnePrincipalReduction',
              'annualPreTaxCashFlow',
              'marketAppreciationAmount',
            ],
            calcFunction: (downPaymentCash, closingCost, ...args) => {
              const sum = getSum(...args);
              return sum;
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'ROI (%)',
            unit: '%',
            name: 'totalRoiYear1',
            description:
              'Percentage based on the total cash invested on purchase',
            formula: (data) => ['=', data],
            emphasize: true,
            dynamic: true,
            groupEnd: true,
            valueType: 'calculatedPercent',
            derivedFrom: [
              'downPaymentAmount',
              'totalCashRequiredToClose',
              'yearOnePrincipalReduction',
              'annualPreTaxCashFlow',
              'marketAppreciationAmount',
            ],
            calcFunction: (downPaymentCash, closingCost, ...args) => {
              const sum = getSum(...args);
              return yearlyPercentFromYearlyValue(sum, [
                downPaymentCash,
                closingCost,
              ]);
            },
          },
          {
            type: FormUIOptions.Number,
            unit: '%',
            label: 'Cap Rate (Purchase Price)',
            name: 'capRate',
            emphasize: true,
            groupEnd: true,
            valueType: 'calculatedPercent',
            derivedFrom: ['monthlyNOI', 'purchasePrice'],
            calcFunction: (monthlyNOI, purchasePrice) => {
              if (isValue(monthlyNOI, purchasePrice)) {
                return Math.round((monthlyNOI / purchasePrice) * 10000) / 100;
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Header,
            label: 'Equity',
          },
          {
            type: FormUIOptions.Money,
            label: 'Property Value',
            description:
              'Estimated property value, starting from the estimated value on purchase.',
            name: 'marketPurchasePrice',
            layout: 'default',
            valueType: 'yearlyDollar',
            multiplier: 'passiveAppreciation',
          },
          {
            type: FormUIOptions.Money,
            label: 'Loan Balance',
            name: 'totalMortgageAmount',
            layout: 'default',
            valueType: 'yearlyDollar',
            subtractor: 'yearOnePrincipalReduction',
            returnNull: 'cashPurchase',
            formula: (data) => ['-', data],
          },
          {
            type: FormUIOptions.Number,
            unit: '%',
            label: 'Loan-to-Value (LTV)',
            name: 'loanToValue',
            layout: 'default',
            valueType: 'yearlyDollar',
            derivedFrom: [
              'marketPurchasePrice',
              'totalMortgageAmount',
              'cashPurchase',
            ],
            small: true,
            calcFunction: (
              purchasePrice,
              totalMortgageAmount,
              cashPurchase
            ) => {
              if (cashPurchase) return null;
              if (isValue(purchasePrice, totalMortgageAmount)) {
                return (
                  Math.round((totalMortgageAmount / purchasePrice) * 10000) /
                  100
                );
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Money,
            unit: '%',
            label: 'Total Equity',
            name: 'totalEquity',
            layout: 'default',
            valueType: 'yearlyDollar',
            derivedFrom: [
              'marketPurchasePrice',
              'totalMortgageAmount',
              'cashPurchase',
            ],
            total: true,
            groupEnd: true,
            emphasize: true,
            calcFunction: (
              purchasePrice,
              totalMortgageAmount,
              cashPurchase
            ) => {
              if (cashPurchase) return purchasePrice;
              if (isValue(purchasePrice, totalMortgageAmount)) {
                return purchasePrice - totalMortgageAmount;
              } else {
                return null;
              }
            },
            formula: (data) => ['=', data],
          },
          {
            type: FormUIOptions.Money,
            label: 'Cumulative Cash Flow',
            layout: 'default',
            name: 'cumulativeCashFlow',
            formula: (data) => ['+', data],
            derivedFrom: ['annualPreTaxCashFlow', 'cumulativeCashFlow', 'year'],
            calcFunction: (annualPreTaxCashFlow, cumulativeCashFlow, year) => {
              if (year === 1) return annualPreTaxCashFlow;

              if (isValue(annualPreTaxCashFlow, cumulativeCashFlow)) {
                return annualPreTaxCashFlow + cumulativeCashFlow;
              } else {
                return 0;
              }
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Total Cash Invested',
            name: 'totalCashInvestedOnPurchase',
            layout: 'default',
            valueType: 'yearlyDollar',
            formula: (data) => ['-', data],
          },
          {
            type: FormUIOptions.Money,
            label: 'Total Profit',
            name: 'totalProfit',
            layout: 'default',
            emphasize: true,
            total: true,
            dynamic: true,
            formula: (data) => ['=', data],
            derivedFrom: [
              'cumulativeCashFlow',
              'totalEquity',
              'totalCashInvestedOnPurchase',
            ],
            calcFunction: (
              cumulativeCashFlow,
              totalEquity,
              totalCashInvestedOnPurchase
            ) => {
              if (
                isValue(
                  cumulativeCashFlow,
                  totalEquity,
                  totalCashInvestedOnPurchase
                )
              ) {
                return (
                  cumulativeCashFlow + totalEquity - totalCashInvestedOnPurchase
                );
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Sale Costs',
            description:
              'Assumed cost of selling the property based on the sale price and sale cost percentage',
            unit: '$',
            layout: 'default',
            name: 'saleCostAmount',
            derivedFrom: ['saleCostPercent', 'marketPurchasePrice'],
            calcFunction: (saleCostPercent, marketPurchasePrice) => {
              if (isValue(saleCostPercent, marketPurchasePrice)) {
                return (saleCostPercent / 100) * marketPurchasePrice;
              } else {
                return null;
              }
            },

            small: true,
          },
          {
            type: FormUIOptions.Money,
            label: 'Total Profit (Sale Pre-Tax)',
            description: 'Total Profit minus Sale Costs',
            unit: '$',
            layout: 'default',
            name: 'totalProfitSale',
            derivedFrom: ['totalProfit', 'saleCostAmount'],
            calcFunction: (totalProfit, saleCostAmount) => {
              if (isValue(totalProfit, saleCostAmount)) {
                return totalProfit - saleCostAmount;
              } else {
                return null;
              }
            },
          },
        ],
      },
      {
        sectionLabel: 'BRRRR Scenario',
        sectionValue: 'brrrrScenario',
        sectionDescription: 'Projections for BRRRR Scenario',
        projectionSection: true,
        mainField: {
          type: FormUIOptions.Number,
          unit: '%',
          name: 'capRate',
          label: 'Cap Rate',
          layout: 'default',
          dynamic: true,
        },
        schema: [
          {
            type: FormUIOptions.Header,
            label: 'Income',
          },
          {
            type: FormUIOptions.Money,
            label: 'Gross Rent',
            layout: 'default',
            name: 'totalRentalIncome',
            altName: 'totalRentalIncomeBuyAndHoldProjection',
            valueType: 'monthlyDollar',
            multiplier: 'incomeIncrease',
          },
          {
            type: FormUIOptions.Money,
            label: 'Vacancy',
            layout: 'default',
            name: 'vacancy',
            formula: (data) => ['-', data],
            valueType: 'monthlyDollar',
            derivedFrom: ['vacancyRate', 'totalRentalIncome'],
            calcFunction: (vacancyRate, totalRentalIncome) => {
              if (isValue(vacancyRate, totalRentalIncome)) {
                return Math.round(vacancyRate * totalRentalIncome * 100) / 100;
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Additional Income',
            layout: 'default',
            name: 'totalOtherIncome',
            altName: 'totalAdditionalIncomeBuyAndHoldProjection',
            formula: (data) => ['+', data],
            valueType: 'monthlyDollar',
            multiplier: 'incomeIncrease',
          },
          {
            type: FormUIOptions.Money,
            label: 'Gross Operating Income',
            layout: 'default',
            name: 'monthlyGOI',
            formula: (data) => ['=', data],
            emphasize: true,
            total: true,
            valueType: 'monthlyDollar',
            multiplier: 'incomeIncrease',
          },
          {
            type: FormUIOptions.Number,
            label: 'Income Increase',
            description: 'Assumed yearly increase in income',
            unit: '%',
            layout: 'default',
            name: 'yearlyIncomeIncrease',
            valueType: 'constantPercent',
            groupEnd: true,
            small: true,
          },
          {
            type: FormUIOptions.Header,
            label: 'Expenses',
          },
          {
            type: FormUIOptions.Money,
            label: 'Total Operating Expenses',
            layout: 'default',
            name: 'totalOperatingExpenses',
            altName: 'totalMonthlyOperatingExpensesProjection',
            formula: (data) => ['-', data],
            valueType: 'monthlyDollar',
            multiplier: 'expensesIncrease',
          },
          {
            type: FormUIOptions.Number,
            label: 'Expense Increase',
            unit: '%',
            layout: 'default',
            description: 'Assumed yearly increase in expenses',
            name: 'yearlyExpenseIncrease',
            valueType: 'constantPercent',
            small: true,
          },
          {
            type: FormUIOptions.Money,
            label: 'Rehab / Carrying (Cash)',
            layout: 'default',
            name: 'rehabCostUnfinanced',
            derivedFrom: ['rehabCostUnfinanced', 'rehabLength', 'year'],
            calcFunction: (rehabCostUnfinanced, rehabLength, year) => {
              if (year === 1) {
                if (rehabLength / 12 > 1) {
                  return (
                    (rehabCostUnfinanced / rehabLength) * (rehabLength % 12)
                  );
                }
                return rehabCostUnfinanced;
              }
              return null;
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Net Operating Income',
            layout: 'default',
            name: 'netMonthlyIncome',
            altName: 'monthlyNOI',
            formula: (data) => ['=', data],
            emphasize: true,
            total: true,
            groupEnd: true,
            valueType: 'monthlyDollar',
            derivedFrom: [
              'monthlyGOI',
              'totalOperatingExpenses',
              'rehabCostUnfinanced',
            ],
            calcFunction: (
              monthlyGOI,
              totalMonthlyOperatingExpenses,
              rehabCostUnfinanced
            ) => {
              const rehabCost = rehabCostUnfinanced || 0;

              if (isValue(monthlyGOI, totalMonthlyOperatingExpenses)) {
                const totalExpenses = totalMonthlyOperatingExpenses + rehabCost;

                return Math.round((monthlyGOI - totalExpenses) * 100) / 100;
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Header,
            label: 'Cash Flow',
          },
          {
            type: FormUIOptions.Money,
            label: 'Loan Payments',
            layout: 'default',
            name: 'newMonthlyPayment',
            altName: 'monthlyDebtService',
            description: 'Total of all mortgage payments',
            formula: (data) => ['-', data],
            valueType: 'monthlyDollar',
            derivedFrom: [
              'newAmortization',
              'newMonthlyPayment',
              'newMortgageInterestOnly',
              'newInterestOnlyLoanPeriod',
              'BRRRRAdditionalFinancing',
              'year',
            ],
            calcFunction: (
              amortization,
              monthlyPayment,
              mortgageInterestOnly,
              interestOnlyLoanPeriod,
              BRRRRAdditionalFinancing,
              year
            ) => {
              let firstMonthlyPayment = 0;
              let additionalPayments = 0;

              if (mortgageInterestOnly) {
                if (year <= Math.floor(interestOnlyLoanPeriod)) {
                  firstMonthlyPayment = monthlyPayment;
                }
              } else {
                if (year <= amortization) {
                  firstMonthlyPayment = monthlyPayment;
                }
              }

              if (Array.isArray(BRRRRAdditionalFinancing)) {
                BRRRRAdditionalFinancing.forEach((item) => {
                  const {
                    mortgageInterestOnlyTable,
                    interestOnlyLoanPeriodTable,
                    monthlyPaymentTable,
                    amortizationTable,
                  } = item || {};

                  if (mortgageInterestOnlyTable) {
                    if (year <= interestOnlyLoanPeriodTable) {
                      additionalPayments += monthlyPaymentTable;
                    }
                  } else {
                    if (year <= amortizationTable) {
                      additionalPayments += monthlyPaymentTable;
                    }
                  }
                });
              }
              return (firstMonthlyPayment + additionalPayments) * 12;
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Balloon Payments',
            layout: 'default',
            name: 'balloonPayment',
            description:
              'Total of all balloon payments (if any) paid at the end of the loan period',
            formula: (data) => ['-', data],
            derivedFrom: [
              'newMortgageAmount',
              'newMortgageInterestOnly',
              'newInterestOnlyMonthly',
              'newInterestOnlyLoanPeriod',
              'newInterestRate',
              'BRRRRAdditionalFinancing',
              'year',
            ],
            calcFunction: (
              mortgageAmount,
              mortgageInterestOnly,
              interestOnlyMonthly,
              interestOnlyLoanPeriod,
              interestRate,
              BRRRRAdditionalFinancing,
              year
            ) => {
              let firstMortgageBalloon = 0;
              let additionalBalloon = 0;

              if (
                year === Math.floor(interestOnlyLoanPeriod) &&
                mortgageInterestOnly
              ) {
                firstMortgageBalloon = mortgageAmount;
                if (!interestOnlyMonthly) {
                  firstMortgageBalloon +=
                    mortgageAmount *
                    (interestRate / 100) *
                    interestOnlyLoanPeriod;
                }
              }

              if (Array.isArray(BRRRRAdditionalFinancing)) {
                BRRRRAdditionalFinancing.forEach((item) => {
                  const {
                    mortgageInterestOnlyTable,
                    interestOnlyLoanPeriodTable,
                    interestRateTable,
                    mortgageAmountTable,
                    interestOnlyMonthlyTable,
                  } = item || {};

                  if (
                    year === Math.floor(interestOnlyLoanPeriodTable) &&
                    mortgageInterestOnlyTable
                  ) {
                    additionalBalloon += mortgageAmountTable;
                    if (!interestOnlyMonthlyTable) {
                      additionalBalloon +=
                        mortgageAmountTable *
                        (interestRateTable / 100) *
                        interestOnlyLoanPeriodTable;
                    }
                  }
                });
              }

              return firstMortgageBalloon + additionalBalloon;
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Rehab Loan Payments',
            layout: 'default',
            name: 'totalRehabFinancingMonthlyPayment',
            formula: (data) => ['-', data],
            valueType: 'monthlyDollar',
            derivedFrom: [
              'amortizationRehab',
              'monthlyPaymentRehab',
              'mortgageInterestOnlyRehab',
              'interestOnlyLoanPeriodRehab',
              'rehabCostFinancing',
              'payOffOnRefinanceRehab',
              'year',
              'isRehabFinanced',
              'mortgageAmountRehab',
              'interestOnlyMonthlyRehab',
              'interestRateRehab',
              'rehabLength',
            ],
            calcFunction: (
              amortization,
              monthlyPayment,
              mortgageInterestOnly,
              interestOnlyLoanPeriod,
              rehabCostFinancing,
              payOffOnRefinanceRehab,
              year,
              isRehabFinanced,
              mortgageAmount,
              interestOnlyMonthly,
              interestRate,
              rehabLength
            ) => {
              if (!isRehabFinanced) return 0;

              const rehabYears = rehabLength ? Math.floor(rehabLength / 12) : 0;
              year = year + rehabYears;
              let firstMonthlyPayment = 0;
              let additionalPayments = 0;
              let totalMortgagePayments = 0;
              let balloonPayment = 0;

              if (!payOffOnRefinanceRehab) {
                if (mortgageInterestOnly) {
                  if (year <= Math.floor(interestOnlyLoanPeriod)) {
                    firstMonthlyPayment = monthlyPayment;
                  }
                } else {
                  if (year <= amortization) {
                    firstMonthlyPayment = monthlyPayment;
                  }
                }

                if (
                  year === Math.floor(interestOnlyLoanPeriod) &&
                  mortgageInterestOnly
                ) {
                  balloonPayment = mortgageAmount;
                  if (!interestOnlyMonthly) {
                    balloonPayment +=
                      mortgageAmount *
                      (interestRate / 100) *
                      interestOnlyLoanPeriod;
                  }
                }
              }

              if (Array.isArray(rehabCostFinancing)) {
                const financingArr = rehabCostFinancing.filter(
                  (item) => !item.payOffOnRefinanceTable
                );

                financingArr.forEach((item) => {
                  const {
                    mortgageInterestOnlyTable,
                    interestOnlyLoanPeriodTable,
                    interestRateTable,
                    mortgageAmountTable,
                    interestOnlyMonthlyTable,
                    monthlyPaymentTable,
                    amortizationTable,
                  } = item || {};

                  if (mortgageInterestOnlyTable) {
                    if (year <= interestOnlyLoanPeriodTable) {
                      additionalPayments += monthlyPaymentTable;
                    }
                  } else {
                    if (year <= amortizationTable) {
                      additionalPayments += monthlyPaymentTable;
                    }
                  }

                  if (
                    year === Math.floor(interestOnlyLoanPeriodTable) &&
                    mortgageInterestOnlyTable
                  ) {
                    balloonPayment += mortgageAmountTable;
                    if (!interestOnlyMonthlyTable) {
                      balloonPayment +=
                        mortgageAmountTable *
                        (interestRateTable / 100) *
                        interestOnlyLoanPeriodTable;
                    }
                  }
                });
              }
              totalMortgagePayments =
                (firstMonthlyPayment + additionalPayments) * 12;
              return totalMortgagePayments + balloonPayment;
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Cash Flow',
            layout: 'default',
            name: 'newMonthlyPreTaxCashFlow',
            altName: 'monthlyPreTaxCashFlowBuyAndHold',
            formula: (data) => ['=', data],
            emphasize: true,
            total: true,
            dynamic: true,
            groupEnd: true,
            valueType: 'monthlyDollar',
            derivedFrom: [
              'monthlyGOI',
              'totalOperatingExpenses',
              'newMonthlyPayment',
              'totalRehabFinancingMonthlyPayment',
              'balloonPayment',
            ],
            calcFunction: (
              monthlyGOI,
              totalMonthlyOperatingExpenses,
              newMonthlyPayment,
              totalRehabFinancingMonthlyPayment,
              balloonPayment
            ) => {
              if (!balloonPayment) balloonPayment = 0;

              if (
                isValue(
                  monthlyGOI,
                  totalMonthlyOperatingExpenses,
                  newMonthlyPayment
                )
              ) {
                return (
                  Math.round(
                    (monthlyGOI -
                      newMonthlyPayment -
                      totalMonthlyOperatingExpenses -
                      totalRehabFinancingMonthlyPayment -
                      balloonPayment) *
                      100
                  ) / 100
                );
              } else {
                return null;
              }
            },
          },

          {
            type: FormUIOptions.Header,
            label: 'Key Metrics',
          },
          {
            type: FormUIOptions.Number,
            label: 'Cash on Cash',
            layout: 'default',
            name: 'cashOnCash',
            unit: '%',
            derivedFrom: ['newMonthlyPreTaxCashFlow', 'cashOut'],
            calcFunction: (newMonthlyPreTaxCashFlow, cashOut) => {
              if (!isValue(newMonthlyPreTaxCashFlow, cashOut)) return null;

              return (
                yearlyPercentFromYearlyValue(newMonthlyPreTaxCashFlow, [
                  cashOut < 0 ? -cashOut : 0,
                ]) || ' -'
              );
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Principal Recapture',
            layout: 'default',
            name: 'newYearOnePrincipalReduction',
            altName: 'yearOnePrincipalReduction',
            formula: (data) => ['+', data],
            valueType: 'yearlyDollar',
            derivedFrom: [
              'newYearOnePrincipalReduction',
              'newInterestRate',
              'newMortgageAmount',
              'newFirstMonthlyPayment',
              'year',
              'newAmortization',
              'newMortgageInterestOnly',
              'newInterestOnlyLoanPeriod',
              'BRRRRAdditionalFinancing',
            ],
            calcFunction: (
              yearOnePrincipalReduction,
              interestRate,
              mortgageAmount,
              monthlyPayment,
              year,
              amortization,
              mortgageInterestOnly,
              interestOnlyLoanPeriod,
              BRRRRAdditionalFinancing
            ) => {
              if (!isValue(year)) year = 1;
              if (year === 1) return yearOnePrincipalReduction;

              let firstMortgageReduction = 0;
              let additionalReduction = 0;

              if (isValue(interestRate, mortgageAmount, monthlyPayment)) {
                if (year <= amortization && !mortgageInterestOnly) {
                  const firstMortgageOutstandingAmount =
                    mortgagePrincipalOutstanding(
                      interestRate,
                      getNumberFromString(mortgageAmount),
                      getNumberFromString(monthlyPayment),
                      year * 12
                    );

                  const firstMortagePrincipalOutstandingPrev =
                    year === 1
                      ? mortgageAmount
                      : mortgagePrincipalOutstanding(
                          interestRate,
                          getNumberFromString(mortgageAmount),
                          getNumberFromString(monthlyPayment),
                          (year - 1) * 12
                        );

                  const prevYearsReduction =
                    Math.round(
                      getNumberFromString(mortgageAmount) * 100 -
                        firstMortagePrincipalOutstandingPrev * 100
                    ) / 100;

                  firstMortgageReduction =
                    Math.round(
                      getNumberFromString(mortgageAmount) * 100 -
                        firstMortgageOutstandingAmount * 100
                    ) /
                      100 -
                    prevYearsReduction;
                }

                if (
                  year === Math.floor(interestOnlyLoanPeriod) &&
                  mortgageInterestOnly
                ) {
                  firstMortgageReduction = mortgageAmount;
                }

                if (Array.isArray(BRRRRAdditionalFinancing)) {
                  BRRRRAdditionalFinancing.forEach((item) => {
                    const {
                      mortgageInterestOnlyTable,
                      interestOnlyLoanPeriodTable,
                      interestRateTable,
                      monthlyPaymentTable,
                      amortizationTable,
                      mortgageAmountTable,
                    } = item || {};

                    let mortgageReduction = 0;

                    if (
                      !mortgageInterestOnlyTable &&
                      year <= amortizationTable
                    ) {
                      const outstandingAmount = mortgagePrincipalOutstanding(
                        interestRateTable,
                        getNumberFromString(mortgageAmountTable),
                        getNumberFromString(monthlyPaymentTable),
                        year * 12
                      );

                      const outstandingPrev =
                        year === 1
                          ? mortgageAmount
                          : mortgagePrincipalOutstanding(
                              interestRateTable,
                              getNumberFromString(mortgageAmountTable),
                              getNumberFromString(monthlyPaymentTable),
                              (year - 1) * 12
                            );

                      const prevYearsReduction =
                        Math.round(
                          getNumberFromString(mortgageAmountTable) * 100 -
                            outstandingPrev * 100
                        ) / 100;

                      mortgageReduction =
                        Math.round(
                          getNumberFromString(mortgageAmountTable) * 100 -
                            outstandingAmount * 100
                        ) /
                          100 -
                        prevYearsReduction;
                    }

                    if (
                      mortgageInterestOnlyTable &&
                      typeof interestOnlyLoanPeriodTable === 'number' &&
                      year === Math.floor(interestOnlyLoanPeriodTable)
                    ) {
                      mortgageReduction = mortgageAmountTable;
                    }

                    additionalReduction += mortgageReduction;
                  });
                }

                return firstMortgageReduction + additionalReduction;
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Passive Appreciation',
            layout: 'default',
            name: 'marketAppreciationAmount',
            formula: (data) => ['+', data],
            valueType: 'yearlyDollar',
            derivedFrom: [
              'marketAppreciationAmount',
              'newMarketValue',
              'marketAppreciationPercent',
              'year',
            ],
            calcFunction: (
              marketAppreciationAmount,
              propertyValueProjection,
              passiveAppreciation,
              year
            ) => {
              if (year === 1) {
                return marketAppreciationAmount;
              }
              return propertyValueProjection * (passiveAppreciation / 100);
            },
          },
          //saving these potential future calculations
          // derivedFrom: [
          //   'marketAppreciationAmount',
          //   'newMarketValue',
          //   'marketAppreciationPercent',
          //   'forcedAppreciation',
          //   'marketPurchasePrice',
          //   'rehabLength',
          //   'year',
          // ],
          // calcFunction: (
          //   marketAppreciationAmount,
          //   propertyValueProjection,
          //   passiveAppreciation,
          //   forcedAppreciation,
          //   marketPurchasePrice,
          //   rehabLength,
          //   year
          // ) => {
          //   if (year === 1) {
          //     if (rehabLength / 12 < 1) {
          //       return marketAppreciationAmount;
          //     } else {
          //       let cumAppreciation = marketAppreciationAmount;
          //       let appreciation = 0;
          //       const rehabYears = Math.floor(rehabLength / 12);
          //       const rehabMonths = rehabLength % 12;
          //       let propertyValue = marketPurchasePrice;

          //       for (let i = 1; i <= rehabYears + 1; i++) {
          //         if (i === 1) {
          //           appreciation = marketAppreciationAmount;
          //         } else if (i <= rehabYears) {
          //           propertyValue += (forcedAppreciation / rehabLength) * 12;
          //           appreciation =
          //             propertyValue * (passiveAppreciation / 100);
          //           propertyValue += appreciation;

          //           cumAppreciation += appreciation;
          //         } else if (i === rehabYears + 1) {
          //           propertyValue +=
          //             (forcedAppreciation / rehabLength) * rehabMonths;
          //           appreciation =
          //             propertyValue * (passiveAppreciation / 100);
          //           propertyValue += appreciation;
          //           cumAppreciation += appreciation;
          //         }
          //       }
          //       return cumAppreciation;
          //     }
          //   }
          //   return propertyValueProjection * (passiveAppreciation / 100);
          // },
          {
            type: FormUIOptions.Money,
            label: 'Active Appreciation',
            layout: 'default',
            name: 'rehabActiveAppreciation',
            altName: 'rehabActiveAppreciation',
            formula: (data) => ['+', data],
            valueType: 'oneTimeDollar',
            derivedFrom: ['rehabActiveAppreciation', 'rehabLength', 'year'],
            calcFunction: (forcedAppreciation, rehabLength, year) => {
              if (year === 1) {
                if (rehabLength / 12 > 1) {
                  return (
                    (forcedAppreciation / rehabLength) * (rehabLength % 12)
                  );
                }
                return forcedAppreciation;
              }
              return null;
            },
            // valueType: 'yearSpreadDollar',
            // spreader: 'rehabLength',
          },
          {
            type: FormUIOptions.Money,
            unit: '$',
            label: 'ROI ($)',
            layout: 'default',
            name: 'newTotalRoiYear1',
            altName: 'totalROIYear1BuyAndHoldAmount',
            formula: (data) => ['=', data],
            emphasize: true,
            total: true,
            dynamic: true,
            valueType: 'yearlyDollar',
            derivedFrom: [
              'newYearOnePrincipalReduction',
              'marketAppreciationAmount',
              'rehabActiveAppreciation',
              'newMonthlyPreTaxCashFlow',
            ],
            calcFunction: (
              newYearOnePrincipalReduction,
              marketAppreciationAmount,
              forcedAppreciation,
              newMonthlyPreTaxCashFlow
            ) => {
              const totalROI = getSum(
                newYearOnePrincipalReduction,
                marketAppreciationAmount,
                forcedAppreciation,
                newMonthlyPreTaxCashFlow
              );
              return totalROI;
            },
          },
          {
            type: FormUIOptions.Number,
            unit: '%',
            label: 'ROI (%)',
            altName: 'totalROIYear1BuyandHold',
            layout: 'default',
            description:
              'Percentage based on the cash left in the property. If the cash left in is zero, the ROI will be infinite.',
            name: 'newTotalRoiYear1Percent',
            formula: (data) => ['=', data],
            emphasize: true,
            dynamic: true,
            groupEnd: true,
            valueType: 'yearlyDollar',
            derivedFrom: [
              'newYearOnePrincipalReduction',
              'marketAppreciationAmount',
              'rehabActiveAppreciation',
              'newMonthlyPreTaxCashFlow',
              'cashOut',
            ],
            calcFunction: (
              newYearOnePrincipalReduction,
              marketAppreciationAmount,
              forcedAppreciation,
              newMonthlyPreTaxCashFlow,
              cashOut
            ) => {
              const totalROI = getSum(
                newYearOnePrincipalReduction,
                marketAppreciationAmount,
                forcedAppreciation,
                newMonthlyPreTaxCashFlow
              );
              return yearlyPercentFromYearlyValue(totalROI, [
                cashOut < 0 ? -cashOut : 0,
              ]);
            },
          },
          {
            type: FormUIOptions.Number,
            unit: '%',
            label: 'Cap Rate (Purchase)',
            name: 'capRate',
            emphasize: true,
            valueType: 'calculatedPercent',
            derivedFrom: ['netMonthlyIncome', 'purchasePrice'],
            calcFunction: (netMonthlyIncome, purchasePrice) => {
              if (isValue(netMonthlyIncome, purchasePrice)) {
                return (
                  Math.round((netMonthlyIncome / purchasePrice) * 10000) / 100
                );
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Number,
            unit: '%',
            label: 'Cap Rate (Market)',
            name: 'marketCapRate',
            emphasize: true,
            groupEnd: true,
            valueType: 'calculatedPercent',
            derivedFrom: ['netMonthlyIncome', 'newMarketValue'],
            calcFunction: (netMonthlyIncome, purchasePrice) => {
              if (isValue(netMonthlyIncome, purchasePrice)) {
                return (
                  Math.round((netMonthlyIncome / purchasePrice) * 10000) / 100
                );
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Header,
            label: 'Equity',
          },
          {
            type: FormUIOptions.Money,
            label: 'Property Value',
            name: 'newMarketValue',
            altName: 'propertyValueProjection',
            layout: 'default',
            valueType: 'yearlyDollar',
            multiplier: 'passiveAppreciation',
          },
          {
            type: FormUIOptions.Money,
            label: 'Loan Balance',
            name: 'newFinancingTotal',
            altName: 'totalFinancingAmount',
            layout: 'default',
            valueType: 'yearlyDollar',
            subtractor: 'newYearOnePrincipalReduction',
            formula: (data) => ['-', data],
          },
          {
            type: FormUIOptions.Number,
            unit: '%',
            label: 'Loan-to-Value (LTV)',
            name: 'loanToValue',
            layout: 'default',
            valueType: 'yearlyDollar',
            derivedFrom: ['newMarketValue', 'newFinancingTotal'],
            small: true,
            calcFunction: (newMarketValue, newMortgageTotal) => {
              if (isValue(newMarketValue, newMortgageTotal)) {
                return (
                  Math.round((newMortgageTotal / newMarketValue) * 10000) / 100
                );
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Money,
            unit: '%',
            label: 'Total Equity',
            name: 'equityInProperty',
            altName: 'totalEquity',
            layout: 'default',
            valueType: 'yearlyDollar',
            derivedFrom: ['newMarketValue', 'newFinancingTotal'],
            total: true,
            groupEnd: true,
            emphasize: true,
            calcFunction: (newMarketValue, newMortgageTotal) => {
              if (isValue(newMarketValue, newMortgageTotal)) {
                return newMarketValue - newMortgageTotal;
              } else {
                return null;
              }
            },
            formula: (data) => ['=', data],
          },
          {
            type: FormUIOptions.Money,
            label: 'Cumulative Cash Flow',
            layout: 'default',
            name: 'cumulativeCashFlow',
            formula: (data) => ['+', data],
            derivedFrom: [
              'newMonthlyPreTaxCashFlow',
              'cumulativeCashFlow',
              'year',
            ],
            calcFunction: (annualPreTaxCashFlow, cumulativeCashFlow, year) => {
              if (year === 1) return annualPreTaxCashFlow;

              if (isValue(annualPreTaxCashFlow, cumulativeCashFlow)) {
                return annualPreTaxCashFlow + cumulativeCashFlow;
              } else {
                return 0;
              }
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Cash In / Out',
            name: 'cashOut',
            altName: 'totalCashInvestedBuyAndHoldReverse',
            layout: 'default',
            valueType: 'yearlyDollar',
            dynamic: true,
            formula: (data) => ['+', data],
          },
          {
            type: FormUIOptions.Money,
            label: 'Total Profit',
            name: 'totalProfit',
            layout: 'default',
            emphasize: true,
            total: true,
            dynamic: true,
            formula: (data) => ['=', data],
            derivedFrom: [
              'cumulativeCashFlow',
              'equityInProperty',
              'cashOut',
              'totalRefinancingCosts',
            ],
            calcFunction: (
              cumulativeCashFlow,
              equityInProperty,
              cashOut,
              totalRefinancingCosts
            ) => {
              if (isValue(cumulativeCashFlow, equityInProperty, cashOut)) {
                return (
                  cumulativeCashFlow +
                    equityInProperty +
                    cashOut -
                    totalRefinancingCosts || 0
                );
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Sale Costs',
            description:
              'Assumed cost of selling the property based on the sale price and sale cost percentage',
            unit: '$',
            layout: 'default',
            name: 'saleCostAmount',
            derivedFrom: ['saleCostPercent', 'newMarketValue'],
            calcFunction: (saleCostPercent, marketPurchasePrice) => {
              if (isValue(saleCostPercent)) {
                return (
                  (saleCostPercent / 100) *
                  (marketPurchasePrice || propertyValueProjection)
                );
              } else {
                return null;
              }
            },

            small: true,
          },
          {
            type: FormUIOptions.Money,
            label: 'Total Profit (Sale Pre-Tax)',
            description: 'Total Profit minus Sale Costs',
            unit: '$',
            layout: 'default',
            name: 'totalProfitSale',
            derivedFrom: ['totalProfit', 'saleCostAmount'],
            calcFunction: (totalProfit, saleCostAmount) => {
              if (isValue(totalProfit, saleCostAmount)) {
                return totalProfit - saleCostAmount;
              } else {
                return null;
              }
            },
          },
        ],
      },
      {
        sectionLabel: 'Buy and Hold (After Rehab)',
        sectionValue: 'buyAndHold',
        sectionDescription:
          'Projections for the property for a buy and hold scenario',
        projectionSection: true,
        onlyBeforeData: true,
        mainField: {
          type: FormUIOptions.Number,
          unit: '%',
          name: 'capRate',
          label: 'Cap Rate',
          layout: 'default',
          dynamic: true,
        },
        schema: [
          {
            type: FormUIOptions.Header,
            label: 'Income',
          },
          {
            type: FormUIOptions.Money,
            label: 'Gross Rent',
            layout: 'default',
            name: 'totalRentalIncomeBuyAndHoldProjection',
            derivedFrom: [
              'totalRentalIncomeBuyAndHoldProjection',
              'totalRentalIncomeBuyAndHold',
              'totalRentalIncomeOnPurchase',
              'rehabLength',
              'yearlyIncomeIncrease',
              'year',
            ],
            calcFunction: (
              totalRentalIncomeBuyAndHoldProjection,
              totalRentalIncomeBuyAndHold,
              totalRentalIncomeOnPurchase,
              rehabLength,
              yearlyIncomeIncrease,
              year
            ) => {
              const yearOnPurchase = (totalRentalIncomeOnPurchase || 0) * 12;
              const yearOnHold = (totalRentalIncomeBuyAndHold || 0) * 12;
              const rehabDifference = yearOnHold - yearOnPurchase;
              const appreciationMultiplier = yearlyIncomeIncrease
                ? 1 + yearlyIncomeIncrease / 100
                : 1;

              if (rehabLength / 12 < 1) {
                if (year === 1) {
                  return yearOnHold * appreciationMultiplier;
                } else {
                  return (
                    totalRentalIncomeBuyAndHoldProjection *
                    appreciationMultiplier
                  );
                }
              } else {
                const rehabYears = Math.floor(rehabLength / 12);
                const rehabMonths = rehabLength % 12;

                if (year <= rehabYears) {
                  if (year === 1) {
                    return (
                      yearOnPurchase + (rehabDifference / rehabLength) * 12
                    );
                  } else {
                    return (
                      totalRentalIncomeBuyAndHoldProjection +
                      (rehabDifference / rehabLength) * 12
                    );
                  }
                }
                if (year === rehabYears + 1) {
                  return (
                    (totalRentalIncomeBuyAndHoldProjection +
                      (rehabDifference / rehabLength) * rehabMonths) *
                    appreciationMultiplier
                  );
                }
                if (year > rehabYears + 1) {
                  return (
                    totalRentalIncomeBuyAndHoldProjection *
                    appreciationMultiplier
                  );
                }
              }
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Vacancy',
            layout: 'default',
            name: 'vacancy',
            formula: (data) => ['-', data],
            valueType: 'monthlyDollar',
            derivedFrom: [
              'vacancyRate',
              'totalRentalIncomeBuyAndHoldProjection',
            ],
            calcFunction: (vacancyRate, rentalIncomePreVacancy) => {
              if (isValue(vacancyRate, rentalIncomePreVacancy)) {
                return (
                  Math.round(vacancyRate * rentalIncomePreVacancy * 100) / 100
                );
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Additional Income',
            layout: 'default',
            name: 'totalAdditionalIncomeBuyAndHoldProjection',
            formula: (data) => ['+', data],
            derivedFrom: [
              'totalAdditionalIncomeBuyAndHoldProjection',
              'totalAdditionalIncomeBuyAndHold',
              'totalAdditionalIncome',
              'rehabLength',
              'yearlyIncomeIncrease',
              'year',
            ],
            calcFunction: (
              totalRentalIncomeBuyAndHoldProjection,
              totalRentalIncomeBuyAndHold,
              totalRentalIncomeOnPurchase,
              rehabLength,
              yearlyIncomeIncrease,
              year
            ) => {
              const yearOnPurchase = (totalRentalIncomeOnPurchase || 0) * 12;
              const yearOnHold = (totalRentalIncomeBuyAndHold || 0) * 12;
              const rehabDifference = yearOnHold - yearOnPurchase;
              const appreciationMultiplier = yearlyIncomeIncrease
                ? 1 + yearlyIncomeIncrease / 100
                : 1;

              if (rehabLength / 12 < 1) {
                if (year === 1) {
                  return yearOnHold * appreciationMultiplier;
                } else {
                  return (
                    totalRentalIncomeBuyAndHoldProjection *
                    appreciationMultiplier
                  );
                }
              } else {
                const rehabYears = Math.floor(rehabLength / 12);
                const rehabMonths = rehabLength % 12;

                if (year <= rehabYears) {
                  if (year === 1) {
                    return (
                      yearOnPurchase + (rehabDifference / rehabLength) * 12
                    );
                  } else {
                    return (
                      totalRentalIncomeBuyAndHoldProjection +
                      (rehabDifference / rehabLength) * 12
                    );
                  }
                }
                if (year === rehabYears + 1) {
                  return (
                    (totalRentalIncomeBuyAndHoldProjection +
                      (rehabDifference / rehabLength) * rehabMonths) *
                    appreciationMultiplier
                  );
                }
                if (year > rehabYears + 1) {
                  return (
                    totalRentalIncomeBuyAndHoldProjection *
                    appreciationMultiplier
                  );
                }
              }
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Gross Operating Income',
            layout: 'default',
            name: 'monthlyGOI',
            formula: (data) => ['=', data],
            emphasize: true,
            total: true,
            derivedFrom: [
              'totalRentalIncomeBuyAndHoldProjection',
              'totalAdditionalIncomeBuyAndHoldProjection',
              'vacancy',
            ],
            calcFunction: (
              totalRentalIncome,
              totalAdditionalIncome,
              vacancy
            ) => {
              if (isValue(totalRentalIncome, totalAdditionalIncome, vacancy)) {
                return totalRentalIncome + totalAdditionalIncome - vacancy;
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Income Increase',
            description: 'Assumed yearly increase in income',
            unit: '%',
            layout: 'default',
            name: 'yearlyIncomeIncrease',
            valueType: 'constantPercent',
            groupEnd: true,
            small: true,
          },
          {
            type: FormUIOptions.Header,
            label: 'Expenses',
          },
          {
            type: FormUIOptions.Money,
            label: 'Total Operating Expenses',
            layout: 'default',
            name: 'totalMonthlyOperatingExpensesProjection',
            formula: (data) => ['-', data],
            derivedFrom: [
              'totalMonthlyOperatingExpensesProjection',
              'totalMonthlyOperatingExpenses',
              'totalMonthlyOperatingExpensesPurchase',
              'rehabLength',
              'yearlyExpenseIncrease',
              'year',
            ],
            calcFunction: (
              totalRentalIncomeBuyAndHoldProjection,
              totalRentalIncomeBuyAndHold,
              totalRentalIncomeOnPurchase,
              rehabLength,
              yearlyIncomeIncrease,
              year
            ) => {
              const yearOnPurchase = (totalRentalIncomeOnPurchase || 0) * 12;
              const yearOnHold = (totalRentalIncomeBuyAndHold || 0) * 12;
              const rehabDifference = yearOnHold - yearOnPurchase;
              const appreciationMultiplier = yearlyIncomeIncrease
                ? 1 + yearlyIncomeIncrease / 100
                : 1;

              if (rehabLength / 12 < 1) {
                if (year === 1) {
                  return yearOnHold * appreciationMultiplier;
                } else {
                  return (
                    totalRentalIncomeBuyAndHoldProjection *
                    appreciationMultiplier
                  );
                }
              } else {
                const rehabYears = Math.floor(rehabLength / 12);
                const rehabMonths = rehabLength % 12;

                if (year <= rehabYears) {
                  if (year === 1) {
                    return (
                      yearOnPurchase + (rehabDifference / rehabLength) * 12
                    );
                  } else {
                    return (
                      totalRentalIncomeBuyAndHoldProjection +
                      (rehabDifference / rehabLength) * 12
                    );
                  }
                }
                if (year === rehabYears + 1) {
                  return (
                    (totalRentalIncomeBuyAndHoldProjection +
                      (rehabDifference / rehabLength) * rehabMonths) *
                    appreciationMultiplier
                  );
                }
                if (year > rehabYears + 1) {
                  return (
                    totalRentalIncomeBuyAndHoldProjection *
                    appreciationMultiplier
                  );
                }
              }
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Expense Increase',
            description: 'Assumed yearly increase in expenses',
            unit: '%',
            layout: 'default',
            name: 'yearlyExpenseIncrease',
            valueType: 'constantPercent',
            small: true,
          },
          {
            type: FormUIOptions.Money,
            label: 'Rehab / Carrying (Cash)',
            layout: 'default',
            name: 'rehabCostUnfinanced',
            valueType: 'yearSpreadDollar',
            spreader: 'rehabLength',
            formula: (data) => ['-', data],
          },
          {
            type: FormUIOptions.Money,
            label: 'Net Operating Income',
            layout: 'default',
            name: 'monthlyNOI',
            formula: (data) => ['=', data],
            emphasize: true,
            total: true,
            groupEnd: true,
            // valueType: 'monthlyDollar',
            derivedFrom: [
              'monthlyGOI',
              'totalMonthlyOperatingExpensesProjection',
              'rehabCostUnfinanced',
            ],
            calcFunction: (
              monthlyGOI,
              totalMonthlyOperatingExpenses,
              totalRehabCarryingGreater12
            ) => {
              if (isValue(monthlyGOI, totalMonthlyOperatingExpenses)) {
                const totalExpenses =
                  totalMonthlyOperatingExpenses +
                  (totalRehabCarryingGreater12 || 0);

                return Math.round((monthlyGOI - totalExpenses) * 100) / 100;
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Header,
            label: 'Cash Flow',
          },
          {
            type: FormUIOptions.Money,
            label: 'Mortgage Payments',
            description: 'Total of all mortgage payments',
            layout: 'default',
            name: 'monthlyDebtService',
            formula: (data) => ['-', data],
            valueType: 'monthlyDollar',
            derivedFrom: [
              'amortization',
              'monthlyPayment',
              'mortgageInterestOnly',
              'interestOnlyLoanPeriod',
              'purchaseAdditionalFinancing',
              'year',
              'cashPurchase',
            ],
            calcFunction: (
              amortization,
              monthlyPayment,
              mortgageInterestOnly,
              interestOnlyLoanPeriod,
              purchaseAdditionalFinancing,
              year,
              cashPurchase
            ) => {
              if (cashPurchase) return 0;
              let firstMonthlyPayment = 0;
              let additionalPayments = 0;

              if (mortgageInterestOnly) {
                if (year <= Math.floor(interestOnlyLoanPeriod)) {
                  firstMonthlyPayment = monthlyPayment;
                }
              } else {
                if (year <= amortization) {
                  firstMonthlyPayment = monthlyPayment;
                }
              }

              if (Array.isArray(purchaseAdditionalFinancing)) {
                purchaseAdditionalFinancing.forEach((item) => {
                  const {
                    mortgageInterestOnlyTable,
                    interestOnlyLoanPeriodTable,
                    monthlyPaymentTable,
                    amortizationTable,
                  } = item || {};

                  if (mortgageInterestOnlyTable) {
                    if (year <= interestOnlyLoanPeriodTable) {
                      additionalPayments += monthlyPaymentTable;
                    }
                  } else {
                    if (year <= amortizationTable) {
                      additionalPayments += monthlyPaymentTable;
                    }
                  }
                });
              }
              return (firstMonthlyPayment + additionalPayments) * 12;
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Balloon Payments',
            layout: 'default',
            name: 'balloonPayment',
            formula: (data) => ['-', data],
            derivedFrom: [
              'mortgageAmount',
              'mortgageInterestOnly',
              'interestOnlyMonthly',
              'interestOnlyLoanPeriod',
              'interestRate',
              'purchaseAdditionalFinancing',
              'year',
              'cashPurchase',
            ],
            calcFunction: (
              mortgageAmount,
              mortgageInterestOnly,
              interestOnlyMonthly,
              interestOnlyLoanPeriod,
              interestRate,
              purchaseAdditionalFinancing,
              year,
              cashPurchase
            ) => {
              if (cashPurchase) return 0;
              let firstMortgageBalloon = 0;
              let additionalBalloon = 0;

              if (
                year === Math.floor(interestOnlyLoanPeriod) &&
                mortgageInterestOnly
              ) {
                firstMortgageBalloon = mortgageAmount;
                if (!interestOnlyMonthly) {
                  firstMortgageBalloon +=
                    mortgageAmount *
                    (interestRate / 100) *
                    interestOnlyLoanPeriod;
                }
              }

              if (Array.isArray(purchaseAdditionalFinancing)) {
                purchaseAdditionalFinancing.forEach((item) => {
                  const {
                    mortgageInterestOnlyTable,
                    interestOnlyLoanPeriodTable,
                    interestRateTable,
                    mortgageAmountTable,
                    interestOnlyMonthlyTable,
                  } = item || {};

                  if (
                    year === Math.floor(interestOnlyLoanPeriodTable) &&
                    mortgageInterestOnlyTable
                  ) {
                    additionalBalloon += mortgageAmountTable;
                    if (!interestOnlyMonthlyTable) {
                      additionalBalloon +=
                        mortgageAmountTable *
                        (interestRateTable / 100) *
                        interestOnlyLoanPeriodTable;
                    }
                  }
                });
              }

              return firstMortgageBalloon + additionalBalloon;
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Rehab Loan Payments',
            layout: 'default',
            name: 'totalRehabFinancingMonthlyPayment',
            formula: (data) => ['-', data],
            valueType: 'monthlyDollar',
            derivedFrom: [
              'amortizationRehab',
              'monthlyPaymentRehab',
              'mortgageInterestOnlyRehab',
              'interestOnlyLoanPeriodRehab',
              'rehabCostFinancing',
              'year',
              'isRehabFinanced',
              'mortgageAmountRehab',
              'interestOnlyMonthlyRehab',
              'interestRateRehab',
            ],
            calcFunction: (
              amortization,
              monthlyPayment,
              mortgageInterestOnly,
              interestOnlyLoanPeriod,
              purchaseAdditionalFinancing,
              year,
              isRehabFinanced,
              mortgageAmount,
              interestOnlyMonthly,
              interestRate
            ) => {
              if (!isRehabFinanced) return 0;
              let firstMonthlyPayment = 0;
              let additionalPayments = 0;
              let totalMortgagePayments = 0;
              let balloonPayment = 0;

              if (mortgageInterestOnly) {
                if (year <= Math.floor(interestOnlyLoanPeriod)) {
                  firstMonthlyPayment = monthlyPayment;
                }
              } else {
                if (year <= amortization) {
                  firstMonthlyPayment = monthlyPayment;
                }
              }

              if (Array.isArray(purchaseAdditionalFinancing)) {
                purchaseAdditionalFinancing.forEach((item) => {
                  const {
                    mortgageInterestOnlyTable,
                    interestOnlyLoanPeriodTable,
                    monthlyPaymentTable,
                    amortizationTable,
                  } = item || {};

                  if (mortgageInterestOnlyTable) {
                    if (year <= interestOnlyLoanPeriodTable) {
                      additionalPayments += monthlyPaymentTable;
                    }
                  } else {
                    if (year <= amortizationTable) {
                      additionalPayments += monthlyPaymentTable;
                    }
                  }
                });
              }
              totalMortgagePayments =
                (firstMonthlyPayment + additionalPayments) * 12;

              if (
                year === Math.floor(interestOnlyLoanPeriod) &&
                mortgageInterestOnly
              ) {
                balloonPayment = mortgageAmount;
                if (!interestOnlyMonthly) {
                  balloonPayment +=
                    mortgageAmount *
                    (interestRate / 100) *
                    interestOnlyLoanPeriod;
                }
              }

              if (Array.isArray(purchaseAdditionalFinancing)) {
                purchaseAdditionalFinancing.forEach((item) => {
                  const {
                    mortgageInterestOnlyTable,
                    interestOnlyLoanPeriodTable,
                    interestRateTable,
                    mortgageAmountTable,
                    interestOnlyMonthlyTable,
                  } = item || {};

                  if (
                    year === Math.floor(interestOnlyLoanPeriodTable) &&
                    mortgageInterestOnlyTable
                  ) {
                    balloonPayment += mortgageAmountTable;
                    if (!interestOnlyMonthlyTable) {
                      balloonPayment +=
                        mortgageAmountTable *
                        (interestRateTable / 100) *
                        interestOnlyLoanPeriodTable;
                    }
                  }
                });
              }

              return totalMortgagePayments + balloonPayment;
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Cash Flow',
            layout: 'default',
            name: 'monthlyPreTaxCashFlowBuyAndHold',
            formula: (data) => ['=', data],
            emphasize: true,
            total: true,
            dynamic: true,
            groupEnd: true,
            valueType: 'yearlyDollar',
            derivedFrom: [
              'monthlyGOI',
              'totalMonthlyOperatingExpensesProjection',
              'monthlyDebtService',
              'totalRehabFinancingMonthlyPayment',
              'balloonPayment',
              'rehabCostUnfinanced',
            ],
            calcFunction: (
              monthlyGOI,
              totalMonthlyOperatingExpenses,
              monthlyDebtService,
              totalRehabFinancingMonthlyPayment,
              balloonPayment,
              totalRehabCarryingGreater12
            ) => {
              if (
                isValue(
                  monthlyGOI,
                  totalMonthlyOperatingExpenses,
                  monthlyDebtService
                )
              ) {
                if (!balloonPayment) balloonPayment = 0;

                totalMonthlyOperatingExpenses +=
                  totalRehabCarryingGreater12 || 0;

                return (
                  Math.round(
                    (monthlyGOI -
                      monthlyDebtService -
                      totalMonthlyOperatingExpenses -
                      totalRehabFinancingMonthlyPayment -
                      balloonPayment) *
                      100
                  ) / 100
                );
              } else {
                return null;
              }
            },
          },

          {
            type: FormUIOptions.Header,
            label: 'Key Metrics',
          },
          {
            type: FormUIOptions.Number,
            label: 'Cash on Cash',
            layout: 'default',
            name: 'cashOnCash',
            valueType: 'yearlyDollar',
            unit: '%',
            derivedFrom: [
              'monthlyPreTaxCashFlowBuyAndHold',
              'totalCashInvestedBuyAndHold',
            ],
            calcFunction: (
              monthlyPreTaxCashFlowBuyAndHold,
              totalCashInvestedBuyAndHold
            ) => {
              return (
                yearlyPercentFromYearlyValue(monthlyPreTaxCashFlowBuyAndHold, [
                  totalCashInvestedBuyAndHold,
                ]) || ' -'
              );
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Principal Recapture',
            layout: 'default',
            name: 'yearOnePrincipalReduction',
            formula: (data) => ['+', data],
            valueType: 'yearlyDollar',
            derivedFrom: [
              'yearOnePrincipalReduction',
              'interestRate',
              'mortgageAmount',
              'monthlyPayment',
              'year',
              'amortization',
              'mortgageInterestOnly',
              'interestOnlyLoanPeriod',
              'cashPurchase',
              'purchaseAdditionalFinancing',
            ],
            calcFunction: (
              yearOnePrincipalReduction,
              interestRate,
              mortgageAmount,
              monthlyPayment,
              year,
              amortization,
              mortgageInterestOnly,
              interestOnlyLoanPeriod,
              cashPurchase,
              purchaseAdditionalFinancing
            ) => {
              if (cashPurchase) return null;

              if (!isValue(year)) year = 1;
              if (year === 1) return yearOnePrincipalReduction;

              let firstMortgageReduction = 0;
              let additionalReduction = 0;

              if (isValue(interestRate, mortgageAmount, monthlyPayment)) {
                if (year <= amortization && !mortgageInterestOnly) {
                  const firstMortgageOutstandingAmount =
                    mortgagePrincipalOutstanding(
                      interestRate,
                      getNumberFromString(mortgageAmount),
                      getNumberFromString(monthlyPayment),
                      year * 12
                    );

                  const firstMortagePrincipalOutstandingPrev =
                    year === 1
                      ? mortgageAmount
                      : mortgagePrincipalOutstanding(
                          interestRate,
                          getNumberFromString(mortgageAmount),
                          getNumberFromString(monthlyPayment),
                          (year - 1) * 12
                        );

                  const prevYearsReduction =
                    Math.round(
                      getNumberFromString(mortgageAmount) * 100 -
                        firstMortagePrincipalOutstandingPrev * 100
                    ) / 100;

                  firstMortgageReduction =
                    Math.round(
                      getNumberFromString(mortgageAmount) * 100 -
                        firstMortgageOutstandingAmount * 100
                    ) /
                      100 -
                    prevYearsReduction;
                }

                if (
                  year === Math.floor(interestOnlyLoanPeriod) &&
                  mortgageInterestOnly
                ) {
                  firstMortgageReduction = mortgageAmount;
                }

                if (Array.isArray(purchaseAdditionalFinancing)) {
                  purchaseAdditionalFinancing.forEach((item) => {
                    const {
                      mortgageInterestOnlyTable,
                      interestOnlyLoanPeriodTable,
                      interestRateTable,
                      monthlyPaymentTable,
                      amortizationTable,
                      mortgageAmountTable,
                    } = item || {};

                    let mortgageReduction = 0;

                    if (
                      !mortgageInterestOnlyTable &&
                      year <= amortizationTable
                    ) {
                      const outstandingAmount = mortgagePrincipalOutstanding(
                        interestRateTable,
                        getNumberFromString(mortgageAmountTable),
                        getNumberFromString(monthlyPaymentTable),
                        year * 12
                      );

                      const outstandingPrev =
                        year === 1
                          ? mortgageAmount
                          : mortgagePrincipalOutstanding(
                              interestRateTable,
                              getNumberFromString(mortgageAmountTable),
                              getNumberFromString(monthlyPaymentTable),
                              (year - 1) * 12
                            );

                      const prevYearsReduction =
                        Math.round(
                          getNumberFromString(mortgageAmountTable) * 100 -
                            outstandingPrev * 100
                        ) / 100;

                      mortgageReduction =
                        Math.round(
                          getNumberFromString(mortgageAmountTable) * 100 -
                            outstandingAmount * 100
                        ) /
                          100 -
                        prevYearsReduction;
                    }

                    if (
                      mortgageInterestOnlyTable &&
                      typeof interestOnlyLoanPeriodTable === 'number' &&
                      year === Math.floor(interestOnlyLoanPeriodTable)
                    ) {
                      mortgageReduction = mortgageAmountTable;
                    }

                    additionalReduction += mortgageReduction;
                  });
                }

                return firstMortgageReduction + additionalReduction;
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Passive Appreciation',
            layout: 'default',
            name: 'marketAppreciationAmount',
            formula: (data) => ['+', data],
            valueType: 'yearlyDollar',
            derivedFrom: [
              'propertyValueProjection',
              'marketAppreciationPercent',
              'year',
              'marketPurchasePrice',
            ],
            calcFunction: (
              propertyValueProjection,
              passiveAppreciation,
              year,
              marketPurchasePrice
            ) => {
              if (year === 1) {
                return marketPurchasePrice * (passiveAppreciation / 100);
              }
              return propertyValueProjection * (passiveAppreciation / 100);
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Active Appreciation',
            layout: 'default',
            name: 'rehabActiveAppreciation',
            formula: (data) => ['+', data],
            valueType: 'yearSpreadDollar',
            spreader: 'rehabLength',
          },
          {
            type: FormUIOptions.Money,
            unit: '$',
            label: 'ROI ($)',
            layout: 'default',
            name: 'totalROIYear1BuyAndHoldAmount',
            formula: (data) => ['=', data],
            emphasize: true,
            total: true,
            dynamic: true,
            valueType: 'yearlyDollar',
            derivedFrom: [
              'yearOnePrincipalReduction',
              'rehabActiveAppreciation',
              'monthlyPreTaxCashFlowBuyAndHold',
              'marketAppreciationAmount',
            ],
            calcFunction: (
              yearOnePrincipalReduction,
              forcedAppreciation,
              monthlyPreTaxCashFlowBuyAndHold,
              marketAppreciationAmount
            ) => {
              const totalROI = getSum(
                yearOnePrincipalReduction,
                marketAppreciationAmount,
                forcedAppreciation,
                monthlyPreTaxCashFlowBuyAndHold
              );
              return totalROI;
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'ROI (%)',
            unit: '%',
            name: 'totalROIYear1BuyandHold',
            groupEnd: true,
            formula: (data) => ['=', data],
            emphasize: true,
            dynamic: true,
            valueType: 'calculatedPercent',
            derivedFrom: [
              'totalCashInvestedBuyAndHold',
              'yearOnePrincipalReduction',
              'rehabActiveAppreciation',
              'monthlyPreTaxCashFlowBuyAndHold',
              'marketAppreciationAmount',
            ],
            calcFunction: (totalCashInvestedBuyAndHold, ...args) => {
              const sum = getSum(...args);
              return yearlyPercentFromYearlyValue(sum, [
                totalCashInvestedBuyAndHold,
              ]);
            },
          },
          {
            type: FormUIOptions.Number,
            unit: '%',
            label: 'Cap Rate (Purchase)',
            name: 'capRate',
            emphasize: true,
            valueType: 'calculatedPercent',
            derivedFrom: ['monthlyNOI', 'purchasePrice'],
            calcFunction: (monthlyNOI, purchasePrice) => {
              if (isValue(monthlyNOI, purchasePrice)) {
                return Math.round((monthlyNOI / purchasePrice) * 10000) / 100;
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Number,
            unit: '%',
            label: 'Cap Rate (Market)',
            name: 'marketCapRate',
            emphasize: true,
            groupEnd: true,
            valueType: 'calculatedPercent',
            derivedFrom: ['monthlyNOI', 'newMarketValue'],
            calcFunction: (netMonthlyIncome, purchasePrice) => {
              if (isValue(netMonthlyIncome, purchasePrice)) {
                return (
                  Math.round((netMonthlyIncome / purchasePrice) * 10000) / 100
                );
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Header,
            label: 'Equity',
          },
          {
            type: FormUIOptions.Money,
            label: 'Property Value',
            name: 'propertyValueProjection',
            description:
              'Projected property value based on passive appreciation, and forced appreciation over the rehab period',
            unit: '$',
            layout: 'default',
            valueType: 'yearlyDollar',
            derivedFrom: [
              'marketPurchasePrice',
              'propertyValueProjection',
              'rehabActiveAppreciation',
              'marketAppreciationAmount',
              'marketAppreciationPercent',
              'year',
            ],
            calcFunction: (
              marketPurchasePrice,
              propertyValueProjection,
              forcedAppreciationBuyAndHold,
              marketAppreciationAmount,
              passiveAppreciation,
              year
            ) => {
              if (year === 1) {
                return (
                  marketPurchasePrice * (1 + passiveAppreciation / 100) +
                  forcedAppreciationBuyAndHold
                );
              } else {
                return (
                  propertyValueProjection +
                  forcedAppreciationBuyAndHold +
                  marketAppreciationAmount
                );
              }
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Loan Balance',
            name: 'totalFinancingAmount',
            layout: 'default',
            valueType: 'yearlyDollar',
            subtractor: 'yearOnePrincipalReduction',
            returnNull: 'cashPurchase',
            formula: (data) => ['-', data],
          },
          {
            type: FormUIOptions.Number,
            unit: '%',
            label: 'Loan-to-Value (LTV)',
            name: 'loanToValue',
            layout: 'default',
            valueType: 'yearlyDollar',
            derivedFrom: [
              'propertyValueProjection',
              'totalFinancingAmount',
              'cashPurchase',
            ],
            small: true,
            calcFunction: (
              newMarketValue,
              totalMortgageAmount,
              cashPurchase
            ) => {
              if (cashPurchase) return null;
              if (isValue(newMarketValue, totalMortgageAmount)) {
                return (
                  Math.round((totalMortgageAmount / newMarketValue) * 10000) /
                  100
                );
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Money,
            unit: '%',
            label: 'Total Equity',
            name: 'totalEquity',
            layout: 'default',
            valueType: 'yearlyDollar',
            derivedFrom: [
              'propertyValueProjection',
              'totalFinancingAmount',
              'cashPurchase',
            ],
            total: true,
            groupEnd: true,
            emphasize: true,
            calcFunction: (
              newMarketValue,
              totalMortgageAmount,
              cashPurchase
            ) => {
              if (cashPurchase) return newMarketValue;
              if (isValue(newMarketValue, totalMortgageAmount)) {
                return newMarketValue - totalMortgageAmount;
              } else {
                return null;
              }
            },
            formula: (data) => ['=', data],
          },
          {
            type: FormUIOptions.Money,
            label: 'Cumulative Cash Flow',
            layout: 'default',
            name: 'cumulativeCashFlow',
            formula: (data) => ['+', data],
            derivedFrom: [
              'monthlyPreTaxCashFlowBuyAndHold',
              'cumulativeCashFlow',
              'year',
            ],
            calcFunction: (annualPreTaxCashFlow, cumulativeCashFlow, year) => {
              if (year === 1) return annualPreTaxCashFlow;

              if (isValue(annualPreTaxCashFlow, cumulativeCashFlow)) {
                return annualPreTaxCashFlow + cumulativeCashFlow;
              } else {
                return 0;
              }
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Total Cash Invested',
            name: 'totalCashInvestedBuyAndHoldReverse',
            layout: 'default',
            valueType: 'yearlyDollar',
            derivedFrom: ['totalCashInvestedBuyAndHold'],
            calcFunction: (totalCashInvestedBuyAndHold) => {
              return totalCashInvestedBuyAndHold
                ? -totalCashInvestedBuyAndHold
                : null;
            },
            // formula: (data) => ['-', data],
          },
          {
            type: FormUIOptions.Money,
            label: 'Total Profit',
            name: 'totalProfit',
            layout: 'default',
            emphasize: true,
            total: true,
            dynamic: true,
            formula: (data) => ['=', data],
            derivedFrom: [
              'cumulativeCashFlow',
              'totalEquity',
              'totalCashInvestedBuyAndHold',
            ],
            calcFunction: (
              cumulativeCashFlow,
              totalEquity,
              totalCashInvestedBuyAndHold
            ) => {
              if (
                isValue(
                  cumulativeCashFlow,
                  totalEquity,
                  totalCashInvestedBuyAndHold
                )
              ) {
                return (
                  cumulativeCashFlow + totalEquity - totalCashInvestedBuyAndHold
                );
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Sale Costs',
            description:
              'Assumed cost of selling the property based on the sale price and sale cost percentage',
            unit: '$',
            layout: 'default',
            name: 'saleCostAmount',
            derivedFrom: ['saleCostPercent', 'propertyValueProjection'],
            calcFunction: (saleCostPercent, marketPurchasePrice) => {
              if (isValue(saleCostPercent)) {
                return (
                  (saleCostPercent / 100) *
                  (marketPurchasePrice || propertyValueProjection)
                );
              } else {
                return null;
              }
            },

            small: true,
          },
          {
            type: FormUIOptions.Money,
            label: 'Total Profit (Sale Pre-Tax)',
            description: 'Total Profit minus Sale Costs',
            unit: '$',
            layout: 'default',
            name: 'totalProfitSale',
            derivedFrom: ['totalProfit', 'saleCostAmount'],
            calcFunction: (totalProfit, saleCostAmount) => {
              if (isValue(totalProfit, saleCostAmount)) {
                return totalProfit - saleCostAmount;
              } else {
                return null;
              }
            },
          },
        ],
      },
    ],
  },
};
